import { Workbook } from 'exceljs';
import type { FormConfig } from '../../config/types';
import { extractConditionalQuestions, normalizeQuarterCode } from '../../utils/config-utils';
import { getScsBusinessObjectives } from '../../utils/scs-objectives-pdf';
import { downloadExcelBuffer } from '../../utils/list-page-excel/workbook';
import { getExcelTemplateFilename } from '../../utils/excel-template-filename';
import {
  fetchModerationPreview,
  type ModerationPreview,
  type ModerationReportMetadata,
  type ModerationRow,
} from './service';

const FIELDS = new Set([
  'report-period',
  'person-details-scs-name',
  'person-details-scs-email-address',
  'person-details-job-title',
  'employee-number',
  'person-details-scs-grade',
  'temporary-promotion',
  'person-details-office-location',
  'person-details-profession-primary',
  'person-details-profession-primary-functional',
  'person-details-manager-email',
  'business-area',
  'objective-sharing-method',
  'minimum-standard-finance',
  'minimum-standard-corporate',
  'person-details-reach',
  'minimum-standard-leadership',
  'minimum-standard-diverityinclusion',
  'performance-summary-q1',
  'person-details-office-location-other',
  'person-details-profession-secondary',
  'person-details-profession-secondary-functional',
  'temporary-grade',
  'performance-summary-q2',
  'talent-career-how-long-current-grade-q2',
  'talent-career-development-activity-q2',
  'person-details-scs-grade-temporary-promotion-dates',
]);
const slug = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');

const statusLabel = (form: ModerationReportMetadata) => {
  const state = form.form_state?.trim().toLowerCase();
  if (state === 'expired') return 'Expired';
  if (state === 'rejected') return 'Rejected';
  if (state === 'withdrawn') return 'Withdrawn';
  if (state === 'approval') {
    const quarter = normalizeQuarterCode(form.pending_approval_quarter || form.approval_status);
    return quarter ? `${quarter} Approval` : 'Approval';
  }
  if (state === 'active') {
    const completed =
      normalizeQuarterCode(form.approved_quarter) ||
      (form.approval_status?.match(/^Q([1-4])\s+Complete$/i)?.[0]
        ? normalizeQuarterCode(form.approval_status)
        : '');
    if (completed === 'Q4') return 'Q4 Submitted';
    return `Q${completed ? Number(completed.slice(1)) + 1 : 1} In Progress`;
  }
  if (['submitted', 'approved', 'exported', 'review'].includes(state || '')) return 'Q4 Submitted';
  return 'Q1 In Progress';
};

const scalar = (value: unknown): string | number | boolean | null => {
  if (value === undefined || value === null || value === '') return null;
  const result = Array.isArray(value)
    ? value.join(', ')
    : typeof value === 'object'
      ? JSON.stringify(value)
      : value;
  if (typeof result === 'string' && result.length > 32767) {
    throw new Error('A response exceeds the Excel cell limit. The report could not be generated.');
  }
  return typeof result === 'number' || typeof result === 'boolean' ? result : String(result);
};

export const buildModerationWorkbook = (config: FormConfig, records: ModerationPreview[]) => {
  if (!records.length) throw new Error('No responses match these filters.');
  const maxObjectives = Math.max(
    ...records.map(({ formData }) => getScsBusinessObjectives(formData.answers).length),
  );
  const columns: { header: string; answer?: string; quarter?: string }[] = [
    'form_id',
    'identifier',
    'form_status',
    'created_at',
    'submitted_at',
    'revision_id',
  ].map((header) => ({ header }));
  const seen = new Set<string>();
  for (const section of config.sections) {
    for (const subsection of section.subsections) {
      for (const page of subsection.pages) {
        if (page.name === 'business-objective') {
          for (let number = 1; number <= maxObjectives; number++) {
            for (const kind of ['what', 'how'])
              columns.push({ header: `business_objective_${kind}_${number}` });
          }
        } else if ('questions' in page) {
          for (const question of extractConditionalQuestions(page.questions || [])) {
            const quarter = /^approval-q[24]$/.test(page.name)
              ? page.name.slice(-2).toUpperCase()
              : undefined;
            const header = `${slug(page.name)}.${slug(question.name)}`;
            if ((!FIELDS.has(question.name) && !quarter) || seen.has(header)) continue;
            columns.push({ header, answer: question.name, quarter });
            seen.add(header);
          }
        }
      }
    }
  }
  const workbook = new Workbook();
  const sheet = workbook.addWorksheet('Moderation Report');
  sheet.columns = columns.map(({ header }) => ({
    header,
    key: header,
    width: header.startsWith('business_objective_') ? 48 : 32,
  }));
  for (const { formData, reportMetadata } of records) {
    if (!reportMetadata) throw new Error('Report metadata is unavailable. Refresh and try again.');
    const answers = formData.answers;
    const year = String(answers['report-period'] || reportMetadata.report_period || '');
    const values: Record<string, unknown> = {
      ...reportMetadata,
      form_status: statusLabel(reportMetadata),
      revision_id: reportMetadata.revision_version,
    };
    for (const objective of getScsBusinessObjectives(answers)) {
      values[`business_objective_what_${objective.number}`] = objective.what;
      values[`business_objective_how_${objective.number}`] = objective.how;
    }
    sheet.addRow(
      columns.map(({ header, answer, quarter }) => {
        if (!answer) return scalar(values[header]);
        const source = quarter
          ? { ...answers, ...formData.approvals?.[`${quarter}-${year}`]?.answers }
          : answers;
        return scalar(answer === 'report-period' ? year : source[answer]);
      }),
    );
  }
  sheet.eachRow((row) => {
    row.alignment = { vertical: 'top', wrapText: true };
  });
  sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
  sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1D3557' } };
  sheet.getRow(1).height = 75;
  sheet.views = [{ state: 'frozen', xSplit: 6, ySplit: 1 }];
  sheet.autoFilter = { from: 'A1', to: { row: sheet.rowCount, column: columns.length } };
  return workbook;
};

export const downloadModerationReport = async (
  token: string,
  config: FormConfig,
  rows: ModerationRow[],
) => {
  const targets = [...new Map(rows.map((row) => [row.formId, row])).values()].sort((a, b) =>
    a.scsName.localeCompare(b.scsName),
  );
  const records: ModerationPreview[] = [];
  for (let index = 0; index < targets.length; index += 4) {
    records.push(
      ...(await Promise.all(
        targets.slice(index, index + 4).map(async (row) => {
          const record = await fetchModerationPreview(token, row);
          const year =
            record.formData.answers['report-period'] || record.reportMetadata?.report_period;
          if (
            year !== row.reportYear ||
            record.formData.answers['person-details-scs-grade'] !== row.grade
          ) {
            throw new Error('Responses have changed. Refresh moderation and try again.');
          }
          return record;
        }),
      )),
    );
  }
  const workbook = buildModerationWorkbook(config, records);
  const buffer = await workbook.xlsx.writeBuffer();
  downloadExcelBuffer(buffer as ArrayBuffer, getExcelTemplateFilename('moderationreport'));
};
